import { useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { HeaderControls } from './HeaderControls'
import { MetricsOverview } from './MetricsOverview'
import { usePositionMetrics } from './hooks/usePositionMetrics'
import { buildChartSeries, getCurrentMetrics, getOverviewMetrics } from './utils'
import { Banner } from 'components/Banner/Banner'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { usePositionMetricsHistory } from './hooks/usePositionMetricsHistory'
import { usePostHogEntityViewed } from 'utils/usePostHogEntityViewed'
import { MetricsHistoryGraphContainer } from './MetricsHistoryGraphContainer'

export type ActiveMetric = 'position-size' | 'lp-apr' | 'apr' | 'pnl' | 'interest-rates' | 'debt-rewards'

const Container = styled.div`
  margin-top: 24px;
`

const MetricsContainer = styled.div`
  border: 1px solid ${p => p.theme.colors.divider};
  border-radius: 16px;
`

interface Props {
  positionId: string
  isActivePosition: boolean
  xSymbol: string
  ySymbol: string
  configName: string
}

export const Analytics = ({ positionId, isActivePosition, xSymbol, ySymbol, configName }: Props) => {
  const [activeMetric, setActiveMetric] = useState<ActiveMetric>('position-size')
  const [pnlView, setPnlView] = useState<'pct' | 'usd'>('usd')

  usePostHogEntityViewed('lp:analytics_view', {
    entityId: positionId,
    entityName: configName,
  })

  const {
    data: overviewData,
    isLoading: isLoadingOverviewData,
    isError: isErrorOverviewData,
  } = usePositionMetrics(positionId)

  const {
    data: historyData,
    isLoading: isHistoryDataLoading,
    isError: isHistoryDataError,
  } = usePositionMetricsHistory(positionId)

  const theme = useTheme()

  const overviewMetrics = useMemo(() => {
    return getOverviewMetrics(theme, overviewData)
  }, [overviewData, theme])

  const currentMetrics = useMemo(() => {
    return getCurrentMetrics(theme, xSymbol, ySymbol, activeMetric, overviewData)
  }, [theme, activeMetric, overviewData, xSymbol, ySymbol])

  const {
    data: chartData,
    bridgeKeys,
    realPointCount,
  } = useMemo(
    () => buildChartSeries(activeMetric, historyData, pnlView === 'pct', overviewData, isActivePosition),
    [historyData, activeMetric, pnlView, overviewData, isActivePosition]
  )

  const handleActiveMetricChange = useCallback((metric: ActiveMetric) => setActiveMetric(metric), [])

  const handlePnlViewToggle = useCallback(() => setPnlView(pnlView === 'pct' ? 'usd' : 'pct'), [pnlView])

  const isMobile = useIsMobile()

  const showErrorCurrentMetrics =
    (isErrorOverviewData && !isLoadingOverviewData && isActivePosition) ||
    (!isActivePosition && (isHistoryDataError || historyData?.length === 0))

  const isLoadingOverviewMetrics = isActivePosition ? isLoadingOverviewData : isHistoryDataLoading

  const historyDataError =
    (historyData?.length === 0 && !isHistoryDataLoading) || (isHistoryDataError && !isHistoryDataLoading)

  return (
    <Container>
      {showErrorCurrentMetrics ? (
        <MetricsContainer style={{ marginBottom: 24 }}>
          <Banner variant="warning">
            <Typography variant={isMobile ? 'caption2' : 'body'} fontWeight="semiBold">
              Current metrics are not available for this position
            </Typography>
          </Banner>
        </MetricsContainer>
      ) : (
        <MetricsContainer style={{ marginBottom: 24 }}>
          <MetricsOverview isLoading={isLoadingOverviewMetrics} metricsValues={overviewMetrics} />
        </MetricsContainer>
      )}
      <MetricsContainer>
        {!historyDataError && (
          <HeaderControls
            activeMetric={activeMetric}
            onClick={handleActiveMetricChange}
            pnlView={pnlView}
            onPnlViewToggle={handlePnlViewToggle}
            isLoading={isLoadingOverviewMetrics}
          />
        )}

        <MetricsOverview isLoading={isLoadingOverviewMetrics} metricsValues={currentMetrics} />
        <MetricsHistoryGraphContainer
          data={chartData}
          bridgeKeys={bridgeKeys}
          realPointCount={realPointCount}
          isLoading={isHistoryDataLoading}
          isError={isHistoryDataError}
          addMargin={currentMetrics && currentMetrics.length > 0}
          xSymbol={xSymbol}
          ySymbol={ySymbol}
        />
      </MetricsContainer>
    </Container>
  )
}
